import { useQuery } from 'react-query';

import { queryKeys } from 'src/services/api/constants';
import { apiClient } from 'src/services/api/client';
import type { IProperty } from 'src/services/api/properties/types';
import { GET_PROPERTIES_PATH } from 'src/services/api/properties/constants';

export interface IGetPropertyOptions {
    /**
     * Parent information is not fetched by default.
     * If set to true, also fetch information about all ancestors in a chain as high as it goes.
     */
    include_ancestors?: boolean;
    /**
     * Property facts not fetched by default.
     * If set to true, fetch all facts about the current property known to the system.
     */
    include_facts?: boolean;
}

type IGetPropertyResponse = {
    /**
     * If `includeFacts` is true then `facts` is guaranteed to be populated.
     * If `includeAncestors` is true then `parent` is guaranteed to be populated.
     */
    property: IProperty;
};

export const normalizeOptions = (rawOptions?: IGetPropertyOptions): IGetPropertyOptions => {
    const {
        include_ancestors = false,
        include_facts = false,
    } = rawOptions ?? {};
    return {
        include_ancestors,
        include_facts,
    };
};

/**
 * Load information about a specific property.
 * Ideally we have that information cached.
 * For default options, should reuse the cache in `useProperties`.
 */
export const useProperty = (id?: number | null, options?: IGetPropertyOptions) => {
    // this is for consistent cache keys with use-properties
    const normalizedOptions = normalizeOptions(options);

    return useQuery(
        [queryKeys.properties, id, normalizedOptions],
        async () => {
            const searchParams = new URLSearchParams();
            if (normalizedOptions.include_ancestors) {
                searchParams.append('include_ancestors', '1');
            }
            if (normalizedOptions.include_facts) {
                searchParams.append('include_facts', '1');
            }

            const path = `${GET_PROPERTIES_PATH}/${id}`;
            const res = await apiClient(path, {
                searchParams
            }).json<IGetPropertyResponse>();
            return res.property;
        },
        {
            enabled: !!id
        }
    );
};
